// objectives.ts ↔ game/Player.cpp:1020-1230 — the UI slice of Player.cpp:
// Player::ShowObjectives, the modal driver every mission-end path runs through
// (EndMissionAction SP arm -> gameOverEvent; the in-game menu's OBJECTIVES and
// ABORT/RESTART arms — InputUI.cpp:383-441; gameform/SimUI MP arms at M5).
//
// The canvas ObjectivesForm (briefing + win/lose summary screens) was removed
// (canvas-removal R1); the DOM briefing (domShowObjectives, intercepted earlier
// in gamesession) + the DOM win/lose summary (domObjectivesSummary) render now.
// ShowObjectives stays continuation-shaped — done(nGo) is the C++ `return nGo`.

import type { ShellEnv } from './shellform.ts';
import {
  knGoSuccess,
  knGoAbortLevel,
  knGoRetryLevel,
  knGoInitFailure,
  knGoLoadSavedGame,
} from './event.ts';
import { kidcCancel } from './uikit.ts';
import { ksoObjectives, ksoWinSummary, ksoLoseSummary } from './gameoptions.ts';
import { EnqueueStoryModal, ResetStoryModals } from './simpause.ts';
// ?dom Phase 5 (Form 3): the win/lose SUMMARY read-model + the DOM overlay seam.
// objectives-snapshot only type-imports back into this module + reads
// kcFormalObjectivesMax (call-time), so the cycle resolves under ESM.
import { objectivesSnapshot } from '../dom/ingame/objectives-snapshot.ts';
import type { ObjectivesSnapshot } from '../dom/ingame/objectives-snapshot.ts';

/** The payload the ?dom win/lose SUMMARY overlay renders (HTML/DOM UI shell plan,
 *  Phase 5, Form 3). `kind` selects the result line + button set (win: OK;
 *  lose: RETRY/ABORT/LOAD SAVED); `missionResult` is the precomputed result-line
 *  text ("OBJECTIVES COMPLETED!" / "MISSION FAILED" / "MISSION ABORTED"). The view
 *  routes the chosen kidc through `onChoice` (the shared post-modal handler). */
export interface ObjectivesSummaryInfo {
  snapshot: ObjectivesSnapshot;
  kind: 'win' | 'lose';
  fAborting: boolean;
  /** the MULTIPLAYER summary (kidfMultiplayerWin/LoseSummary) — different result
   *  text ("YOU WIN!"/"YOU LOSE!") and an OK-only button set. */
  mp: boolean;
  missionResult: string;
}

/** The summary RESULT-LINE text (forms.ini.pp kidcMissionResult). Win authors
 *  "OBJECTIVES COMPLETED!" (Player.cpp:425); lose is the static "MISSION FAILED";
 *  fAborting overrides EITHER to "MISSION ABORTED" (objectives.ts:270-272 — the SP
 *  pause-menu abort path runs ksoWinSummary with fAborting). Pure helper so the
 *  canvas (SetText) + DOM (missionResult) presentations show the IDENTICAL text. */
export function summaryResultLine(kind: 'win' | 'lose', fAborting: boolean, mp = false): string {
  // MP summaries (kidfMultiplayerWin/LoseSummary): "YOU WIN!" / "YOU LOSE!" (no abort path).
  if (mp) return kind === 'lose' ? 'YOU LOSE!' : 'YOU WIN!';
  if (fAborting) return 'MISSION ABORTED';
  return kind === 'lose' ? 'MISSION FAILED' : 'OBJECTIVES COMPLETED!';
}

/** The summary button kidc set, in DOM order (forms.ini.pp kidfWin/LoseSummary).
 *  WIN → [OK (kidcCancel)]; LOSE → [RETRY (kidcRestartMission), ABORT
 *  (kidcAbortMission), LOAD SAVED (kidcLoadGame)]. Each routes through the shared
 *  handleChoice → the SAME gameOverEvent nGo. Pure so it can be unit-tested. */
export function summaryButtonIds(kind: 'win' | 'lose', mp = false): number[] {
  // MP win AND lose are OK-only (forms.ini.pp kidfMultiplayerWin/LoseSummary).
  if (mp) return [kidcCancel];
  return kind === 'win' ? [kidcCancel] : [kidcRestartMission, kidcAbortMission, kidcLoadGame];
}

// game/res.h:4-61 — the objectives/summary form ids
export const kidfLoseSummary = 1003;
export const kidfWinSummary = 1010;
export const kidfObjectives = 1025;
export const kidfMultiplayerLoseSummary = 1063;
export const kidfMultiplayerWinSummary = 1064;
export const kidfMultiplayerObjectives = 1065;

// game/res.h:184-187 (the summary form buttons) + :386-410 (objectives ids)
export const kidcLoadGame = 1002;
export const kidcRestartMission = 1004;
export const kidcAbortMission = 1005;
export const kidcMissionTitle = 1001;
export const kidcMissionResult = 1002;
export const kidcStatistics = 1003;
export const kidcObjectiveText1 = 1101;
export const kidcObjectiveStatus1 = 1201;
export const kidcPage1 = 1300;
export const kidcObjectiveInfo = 1301;
export const kidcPage2 = 1400;
export const kidcMobileUnitsKilled = 1401;
export const kidcStructuresKilled = 1402;
export const kidcMobileUnitsLost = 1403;
export const kidcStructuresLost = 1404;
export const kidcCreditsAction = 1405;
export const kidcGameTime = 1406;
export const kidcInfo = 1407;
export const kidcPage3 = 1500;

// game/ht.h:5625
export const kcFormalObjectivesMax = 4;

// mpshared/mpht.h:78-82 (the Player m_wf bits ShowObjectives moves)
export const kfPlrSummaryShown = 0x0200;
export const kfPlrWinner = 0x1000;
export const kfPlrLoser = 0x2000;

// game/soundeffects.h enum Sfx (order-derived indices)
export const ksfxGameLoseLevel = 21;
export const ksfxGameWinLevel = 25;

/** The Player slice ObjectivesForm/ShowObjectives reads (sim/src/player). */
export interface ObjectivesPlayer {
  GetFlags(): number;
  SetFlags(wf: number): void;
  GetFormalObjectiveText(i: number): string | null;
  GetFormalObjectiveStatus(i: number): string | null;
  GetFormalObjectiveInfo(): string | null;
  GetEnemyMobileUnitsKilled(): number;
  GetEnemyStructuresKilled(): number;
  GetMobileUnitsLost(): number;
  GetStructuresLost(): number;
  GetTotalCreditsAcquired(): number;
  GetTotalCreditsConsumed(): number;
}

/** The ggame/gsim/gplrm/gpcptm reaches of ShowObjectives beyond the ShellEnv. */
export interface ObjectivesDeps {
  plr: ObjectivesPlayer; // the Player (gpplrLocal in every M4 path)
  getLevelTitle(): string; // gsim.GetLevel()->GetTitle()
  simTickCount(): number; // gsim.GetTickCount() (sim 1/100s ticks)
  sendWinStats?(): void; // gplrm.SendWinStats (MP transport; M5)
  markComplete?(): void; // gpcptm->MarkComplete(&ggame.GetLastMissionIdentifier())
  skipSaveReinitializeGame?(): void; // ggame.SkipSaveReinitializeGame (app-exit arm)
}

//
// Player::ShowObjectives (game/Player.cpp:1130-1229) — continuation-shaped:
// done(nGo) is the C++ return. fMultiplayer rides env.fMultiplayer.
//

export function ShowObjectives(
  env: ShellEnv,
  deps: ObjectivesDeps,
  so: number,
  _fForceInfoDisplay = false,
  fAborting = false,
  done: ((nGo: number) => void) | null = null,
): void {
  // Latch SkipSaveReinitializeGame the MOMENT a win/lose summary opens — not only when the
  // player later picks a button (handleChoice, below). The mission is over, but the loop
  // hasn't latched fGameOver yet, so the per-tick autosave + the pagehide flush keep
  // re-writing the resume slot for the FINISHED game while the summary is up; backgrounding
  // or closing the tab during the summary would then resurrect the completed mission on the
  // next boot. (ksoObjectives = the mid-mission briefing, which must keep autosaving.)
  if (so === ksoWinSummary || so === ksoLoseSummary) deps.skipSaveReinitializeGame?.();
  // The C++ SP callers all hold gsim.Pause across the (blocking) modal
  // (InputUI.cpp:260-264/:352-450, TriggerActions.cpp:1127-1157's arm), and
  // the block serializes it behind any story modal already up. The sequencer
  // (EnqueueStoryModal, simpause.ts) carries both: one story modal at a time
  // in raise order, sim paused until the LAST one closes. The queue's next()
  // runs AFTER the embedder's done (which may carry the C++ caller's own
  // direct Pause(false)) so the release re-asserts while other story modals
  // remain. MP never pauses (lockstep); the early-return arms below never
  // reach the queue (they show nothing — their C++ callers' Pause pairs were
  // net no-ops).
  let queueNext: (() => void) | null = null;
  const finish = (nGo: number): void => {
    if (done !== null) done(nGo);
    if (queueNext !== null) {
      const next = queueNext;
      queueNext = null;
      next();
    }
  };

  // The default for losing is knGoAbortLevel. This way, if someone exits
  // while the form is up, knGoAbortLevel will be returned and the game
  // won't be saved. The default for winning, is knGoSuccess.

  let idf: number;
  let nGo: number;
  const plr = deps.plr;

  switch (so) {
    case ksoObjectives:
      idf = env.fMultiplayer ? kidfMultiplayerObjectives : kidfObjectives;
      nGo = knGoSuccess;
      break;

    case ksoWinSummary:
      if (plr.GetFlags() & kfPlrSummaryShown) {
        finish(knGoSuccess);
        return;
      }
      plr.SetFlags(plr.GetFlags() | kfPlrSummaryShown | kfPlrWinner);
      idf = env.fMultiplayer ? kidfMultiplayerWinSummary : kidfWinSummary;
      deps.sendWinStats?.(); // gplrm.SendWinStats()
      if (!env.fMultiplayer && !fAborting) {
        // Mark current game complete.
        // Doing it here ensures it is done when the mission is completed.
        deps.markComplete?.(); // gpcptm->MarkComplete(&ggame.GetLastMissionIdentifier())
      }
      nGo = knGoSuccess;
      break;

    case ksoLoseSummary:
      if (plr.GetFlags() & kfPlrSummaryShown) {
        finish(knGoSuccess);
        return;
      }
      plr.SetFlags(plr.GetFlags() | kfPlrSummaryShown | kfPlrLoser);
      idf = env.fMultiplayer ? kidfMultiplayerLoseSummary : kidfLoseSummary;
      deps.sendWinStats?.();
      nGo = knGoAbortLevel;
      break;

    default:
      // Assert("Invalid objective screen type passed to ShowObjectives")
      finish(knGoInitFailure);
      return;
  }

  // The result line the win form authors in kidcMissionResult (Player.cpp:425):
  // "OBJECTIVES COMPLETED!" (win) / "MISSION FAILED" (lose static) — fAborting
  // overrides to "MISSION ABORTED" (objectives.ts:270-272). Computed once (via the
  // pure summaryResultLine helper) so the canvas (SetText on the kidcMissionResult
  // control) and DOM (missionResult) presentations show the IDENTICAL text.
  const isLose = idf === kidfLoseSummary || idf === kidfMultiplayerLoseSummary;
  const isMP = idf === kidfMultiplayerWinSummary || idf === kidfMultiplayerLoseSummary;
  const missionResult = summaryResultLine(isLose ? 'lose' : 'win', fAborting, isMP);

  // The post-modal switch (Player.cpp:1158-1219) — the SIDE-EFFECT/command arm,
  // shared by the canvas (ShellDoModal close) and the DOM summary (view button)
  // presentations so they resolve to the SAME nGo / gameOverEvent. `nGo` is the
  // enclosing default (knGoSuccess win / knGoAbortLevel lose); LoadGame re-shows
  // the form on a null pick (the lbTryAgain loop).
  const handleChoice = (idc: number): void => {
    if (!env.evm.IsAppStopping()) {
      switch (idc) {
        case kidcLoadGame:
          env.pickLoadGameStream?.((pstm) => {
            if (pstm === null) {
              tryAgain(); // goto lbTryAgain
              return;
            }
            env.setSavedGameStream?.(pstm); // gpstmSavedGame = pstm
            finish(knGoLoadSavedGame);
          });
          return;

        case kidcAbortMission:
        case kidcRestartMission:
          nGo = idc === kidcAbortMission ? knGoAbortLevel : knGoRetryLevel;
          break;
      }
    } else {
      // The game is exiting. If the level was lost, then don't save
      // the re-initialize game, otherwise the user will be able
      // to exit while this form is up, restart the game, and advance
      // to the next level.

      if (nGo !== knGoSuccess) {
        deps.skipSaveReinitializeGame?.(); // ggame.SkipSaveReinitializeGame()
      }
    }

    finish(nGo);
  };

  // lbTryAgain: (the kidcLoadGame null-pick retry loop)
  const tryAgain = (): void => {
    // The SP win/lose SUMMARY renders as a DOM overlay over the live canvas (the
    // in-game briefing, ksoObjectives, is intercepted earlier in gamesession and
    // NEVER reaches this branch). DISPLAY-only: the SFX + the side-effects/flags
    // (kfPlrSummaryShown, SetFlags, sendWinStats, markComplete, run before tryAgain)
    // are unchanged, and the chosen button routes through the SAME shared handleChoice.
    // Covers BOTH SP (kidfWin/LoseSummary) AND MP (kidfMultiplayerWin/LoseSummary) —
    // `mp` selects the MP result text ("YOU WIN!"/"YOU LOSE!") + the OK-only buttons.
    if (
      (idf === kidfWinSummary ||
        idf === kidfLoseSummary ||
        idf === kidfMultiplayerWinSummary ||
        idf === kidfMultiplayerLoseSummary) &&
      env.domObjectivesSummary !== undefined
    ) {
      env.sndm.PlaySfx(isLose || fAborting ? ksfxGameLoseLevel : ksfxGameWinLevel);
      const snap = objectivesSnapshot(deps, env.mfrmm.host.getVar);
      env.domObjectivesSummary(
        { snapshot: snap, kind: isLose ? 'lose' : 'win', fAborting, mp: isMP, missionResult },
        handleChoice,
      );
      return;
    }

    // No DOM summary seam wired (e.g. a headless harness): leave the summary
    // PENDING — like the canvas modal that blocked until dismissed, the game does
    // NOT conclude on its own (the story-modal pause stays held until something
    // drives the choice). Production always wires domObjectivesSummary.
  };
  // The MP win/lose SUMMARY is TERMINAL (it ends the game in its dismiss callback). Flush any leaked
  // in-game story modal first so it can NEVER be queued behind one that didn't release the FIFO — which
  // would strand the local player as a deselected observer with no game-over (the reported MP bug).
  if (isMP) ResetStoryModals(env);
  EnqueueStoryModal(env, true, (next) => {
    queueNext = next;
    tryAgain();
  });
}

export { ksoObjectives, ksoWinSummary, ksoLoseSummary };
